import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { MetricCard } from '../../../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../../../TooltipMetricExplanation'
import { ChartHeader } from '../../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../utils'
import { type EmploymentAnalysis } from '../../employmentService'
import {
  PublicPrivateDistributionChart,
  type PublicPrivateDistributionChartRef,
} from '../charts/PublicPrivateDistributionChart'
import {
  JobsPerEstablishmentChart,
  type JobsPerEstablishmentChartRef,
} from '../charts/JobsPerEstablishmentChart'
import {
  TabContentContainer,
  MetricsGrid,
  ChartGrid,
  ChartContainer,
} from '../../EmploymentTab.styles'

export interface BusinessActivityTabProps {
  data: EmploymentAnalysis | null
  t: TFunction
}

export const BusinessActivityTab: React.FC<BusinessActivityTabProps> = ({
  data,
  t,
}) => {
  const publicPrivateChartRef = useRef<PublicPrivateDistributionChartRef>(null)
  const jobsPerEstablishmentChartRef =
    useRef<JobsPerEstablishmentChartRef>(null)

  if (!data) return null

  const { current } = data

  // Calculate public/private percentages
  const publicEstablishmentsPct =
    (current.total_establishments || 0) > 0
      ? ((current.total_establishments_public_sector || 0) /
          (current.total_establishments || 0)) *
        100
      : 0
  const privateEstablishmentsPct =
    (current.total_establishments || 0) > 0
      ? ((current.total_establishments_private_sector || 0) /
          (current.total_establishments || 0)) *
        100
      : 0

  const getGrowthDisplayValue = (evolution: number): string => {
    const percentage = Math.abs(evolution * 100)
    const sign = evolution >= 0 ? '+' : '-'
    return `${sign}${percentage.toFixed(1)}%`
  }

  const getGrowthVariant = (
    evolution: number
  ): 'success' | 'warning' | 'danger' | 'default' => {
    if (evolution > 0.05) return 'success'
    if (evolution > 0) return 'default'
    if (evolution > -0.05) return 'warning'
    return 'danger'
  }

  return (
    <TabContentContainer>
      {/* Establishment Metrics */}
      <MetricsGrid>
        <MetricCard
          title={t('metrics.totalEstablishments.title')}
          value={(current.total_establishments || 0).toLocaleString()}
          subtitle={t('metrics.totalEstablishments.subtitle')}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.totalEstablishments.title')}
              shortDescription={t(
                'tooltips.totalEstablishments.shortDescription'
              )}
              calculation={t('tooltips.totalEstablishments.calculation')}
              importance={t('tooltips.totalEstablishments.importance')}
              limitations={t('tooltips.totalEstablishments.limitations')}
              source={t('tooltips.totalEstablishments.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.establishmentGrowth.title')}
          value={getGrowthDisplayValue(
            current.total_establishments_evolution_10_year || 0
          )}
          subtitle={t('metrics.establishmentGrowth.subtitle')}
          variant={getGrowthVariant(
            current.total_establishments_evolution_10_year || 0
          )}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.establishmentGrowth.title')}
              shortDescription={t(
                'tooltips.establishmentGrowth.shortDescription'
              )}
              calculation={t('tooltips.establishmentGrowth.calculation')}
              importance={t('tooltips.establishmentGrowth.importance')}
              limitations={t('tooltips.establishmentGrowth.limitations')}
              source={t('tooltips.establishmentGrowth.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.publicSectorShare.title')}
          value={`${publicEstablishmentsPct.toFixed(1)}%`}
          subtitle={`${(current.total_establishments_public_sector || 0).toLocaleString()} ${t('common.establishments')}`}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.publicSectorEstablishments.title')}
              shortDescription={t(
                'tooltips.publicSectorEstablishments.shortDescription'
              )}
              calculation={t('tooltips.publicSectorEstablishments.calculation')}
              importance={t('tooltips.publicSectorEstablishments.importance')}
              limitations={t('tooltips.publicSectorEstablishments.limitations')}
              source={t('tooltips.publicSectorEstablishments.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.privateSectorShare.title')}
          value={`${privateEstablishmentsPct.toFixed(1)}%`}
          subtitle={`${(current.total_establishments_private_sector || 0).toLocaleString()} ${t('common.establishments')}`}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.privateSectorEstablishments.title')}
              shortDescription={t(
                'tooltips.privateSectorEstablishments.shortDescription'
              )}
              calculation={t(
                'tooltips.privateSectorEstablishments.calculation'
              )}
              importance={t('tooltips.privateSectorEstablishments.importance')}
              limitations={t(
                'tooltips.privateSectorEstablishments.limitations'
              )}
              source={t('tooltips.privateSectorEstablishments.source')}
            />
          }
        />
      </MetricsGrid>

      {/* Charts */}
      <ChartGrid>
        <ChartContainer>
          <ChartHeader
            title={t('charts.publicPrivate.title')}
            subtitle={t('charts.publicPrivate.subtitle')}
            onDownload={() => {
              const dataUrl = publicPrivateChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.publicPrivate.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <PublicPrivateDistributionChart
            ref={publicPrivateChartRef}
            data={data.trends}
            height={300}
            type="establishments"
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.jobsPerEstablishment.title')}
            subtitle={t('charts.jobsPerEstablishment.subtitle')}
            onDownload={() => {
              const dataUrl =
                jobsPerEstablishmentChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.jobsPerEstablishment.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <JobsPerEstablishmentChart
            ref={jobsPerEstablishmentChartRef}
            data={data.trends}
            height={300}
            t={t}
          />
        </ChartContainer>
      </ChartGrid>
    </TabContentContainer>
  )
}
